/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ArticlePost from '../Components/ArticlePost';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    color: '#2c3e50',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  publishedChip: {
    backgroundColor: '#e6f7ee',
    color: '#2ecc71',
  },
  draftChip: {
    backgroundColor: '#fef5e7',
    color: '#f39c12',
  },
  featuredChip: {
    backgroundColor: '#3498db',
    color: 'white',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  thumbnail: {
    width: 50,
    height: 50,
    borderRadius: 4,
    objectFit: 'cover',
  },
  dialog: {
    minWidth: '600px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '90%',
    },
  },
}));

const AdminDashboard = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchArticles();
  }, [page, rowsPerPage]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://jtimes.zuddo.biz/main/api/articles?page=${page + 1}&per_page=${rowsPerPage}`,
        {
          headers: {
            'x-access-token': token  
          }
        }
      );
      
      if (response.data.status === 'success') {
        setArticles(response.data.articles);
        setTotalCount(response.data.total);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch articles');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (article) => {
    setSelectedArticle(article);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (article) => {
    setSelectedArticle(article);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `https://jtimes.zuddo.biz/main/api/admin/articles/${selectedArticle.id}`,
        {
          headers: {
             'x-access-token': token  
          }
        }
      );
      
      setSuccessMessage('Article deleted successfully');
      setDeleteDialogOpen(false);
      fetchArticles();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to delete article');
    }
  };

  const handleViewArticle = (articleId) => {
    navigate(`/article/${articleId}`);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedArticle(null);
    fetchArticles();
    setSuccessMessage('Article updated successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getStatusChip = (article) => {
    if (article.is_published) {
      return (
        <Chip
          size="small"
          label="Published"
          className={`${classes.statusChip} ${classes.publishedChip}`}
        />
      );
    } else {
      return (
        <Chip
          size="small"
          label="Draft"
          className={`${classes.statusChip} ${classes.draftChip}`}
        />
      );
    }
  };

  if (loading && articles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }


  return (
    <Container className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Article Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/create')}
        >
          New Article
        </Button>
      </Box>

      {error && (
        <Typography severity="error" onClose={() => setError(null)}>
          {error}
        </Typography>
      )}

      {successMessage && (
        <Typography severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Typography>
      )}

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell>
                  {article.multimedia && article.multimedia.length > 0 ? (
                    <img
                    src={'https://app.zuddo.biz/main/api/getimage'+article.multimedia[0].url} 
                     
                      alt={article.title}
                      className={classes.thumbnail}
                    />
                  ) : (
                    <Avatar variant="square" className={classes.thumbnail}>
                      No img
                    </Avatar>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {article.title}
                  </Typography>
                  {article.is_featured && (
                    <Chip
                      size="small"
                      label="Featured"
                      className={`${classes.statusChip} ${classes.featuredChip}`}
                    />
                  )}
                </TableCell>
                <TableCell>{article.author_name}</TableCell>
                <TableCell>{article.category_name || article.section}</TableCell>
                <TableCell>
                  {new Date(article.published_date).toLocaleDateString()}
                </TableCell>
                <TableCell>{getStatusChip(article)}</TableCell>
                <TableCell>{article.view_count}</TableCell>
                <TableCell>
                  <Box className={classes.actionButtons}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewArticle(article.id)}
                      title="View"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(article)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(article)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedArticle?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Article</DialogTitle>
        <DialogContent dividers>
          {selectedArticle && (
            <ArticlePost
              article={selectedArticle}
              onSuccess={handleEditSuccess}
              isEdit={true}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;